<template>
  <div class="user-form">
    <OurGroups>
      <OurGroup>
        <OurInput
          v-model="form.name"
          :label="$t('label.lastName')"
          :placeholder="$t('placeholder.lastName')"
          :error="nameError"
        />

        <OurInput
          v-model="form.email"
          :label="$t('label.email')"
          :placeholder="$t('placeholder.email')"
          :error="emailError"
        />

        <OurPhoneInput
          v-model="form.phone"
          :label="$t('label.phoneNumber')"
          :placeholder="$t('placeholder.phone')"
          :flags="false"
          :error="phoneError"
          dropdown-disabled
        />

        <OurMultiselect
          v-model="form.regions"
          :options="regions"
          :label="$t('label.region')"
          :placeholder="$t('placeholder.selectRegion')"
          item-label="name"
          :error="regionError"
        />

        <OurSelect
          v-model="form.accessGroupId"
          :options="accessGroups"
          :label="$t('label.accessGroup')"
          :placeholder="$t('placeholder.chooseFromList')"
          item-label="name"
        />

        <OurSelect
          v-model="form.storageId"
          :options="storages"
          :label="$t('label.storage')"
          :placeholder="$t('placeholder.selectStorage')"
          item-label="name"
        />

        <OurFileInput
          :label="$t('label.selectPhoto')"
          :files="files"
          preview
          button-size="xl"
          :disabled="!!files.length"
          :allowed-file-types="allowedFileTypes"
          @changeFiles="onChangeFiles"
          @deleteFile="deleteFile"
        />

        <div class="footer-block">
          <OurCheckbox v-model="form.isActive" :label="$t('page.managers.isActive')" />
        </div>
      </OurGroup>
    </OurGroups>
  </div>
</template>

<script>
import ValidationService from "@/services/validation.service";
import { email, required } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import { getRegionsForRegistration } from "@/api/auth";
import { accessGroupsAndStoragesQuery } from "../edit/gql/queries";
import apolloMixin from "@/mixins/apollo.mixin";

const { phoneMaxLength, phoneMinLength } = ValidationService;

export default {
  name: "UsersForm",

  mixins: [apolloMixin],

  props: {
    value: {
      type: Object,
      default: () => {},
    },

    uploadFiles: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      files: [],
      allowedFileTypes: [".jpg", ".jpeg", ".png"],
      regions: [],
      accessGroups: [],
      storages: [],
      form: {
        name: "",
        email: "",
        phone: "",
        regions: [],
        isActive: false,
        image: null,
        accessGroupId: null,
        storageId: null,
      },
    };
  },

  validations: {
    form: {
      name: { required },
      email: { required, email },
      phone: { required, phoneMinLength, phoneMaxLength },
      regions: { required },
    },
  },

  computed: {
    ...mapGetters("user", ["isUserDealerAdmin", "isUserDealer"]),

    nameError() {
      const isDirty = this.$v.form.name.$dirty;
      const isFilled = this.$v.form.name.required;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      }

      return error;
    },

    emailError() {
      const isDirty = this.$v.form.email.$dirty;
      const isFilled = this.$v.form.email.required;
      const isEmail = this.$v.form.email.email;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      } else if (isDirty && !isEmail) {
        error = this.$t("validation.mustBeEmail");
      }

      return error;
    },

    phoneError() {
      const isDirty = this.$v.form.phone.$dirty;
      const isFilled = this.$v.form.phone.required;

      let isMinLength;
      let isMaxLength;

      if (this.form.phone) {
        isMinLength = this.$v.form.phone.phoneMinLength;
        isMaxLength = this.$v.form.phone.phoneMaxLength;
      }

      let error = "";

      if (isDirty) {
        if (!isFilled) error = this.$t("validation.required");
        else if (!isMinLength || !isMaxLength) error = this.$t("validation.phoneLength");
      }

      return error;
    },

    regionError() {
      const isDirty = this.$v.form.regions.$dirty;
      const isFilled = this.$v.form.regions.required;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      }

      return error;
    },
  },

  watch: {
    value: {
      handler: "getForm",
      deep: true,
    },
    uploadFiles: {
      handler: "getUploadFiles",
      deep: true,
    },
    files: {
      handler: "getFormFiles",
      deep: true,
    },
  },

  mounted() {
    this.getRegions();
    this.getAccessGroupsAndStorages();
    this.getForm();
  },

  methods: {
    getForm() {
      if (this.value) {
        this.form = { ...this.value };

        const { image } = this.value;

        if (image && image.link) {
          this.files = [{ id: image?.id, name: image?.name, imageUrl: image.link }];
        }
      }
    },

    async getAccessGroupsAndStorages() {
      const { accessGroups, storages } = await this.$get(accessGroupsAndStoragesQuery);

      this.accessGroups = accessGroups;
      this.storages = storages;
    },

    async getRegions() {
      const regions = await getRegionsForRegistration();

      this.regions = regions?.map((item) => {
        return {
          id: `${item.id}`,
          name: item.name.ua,
        };
      });
    },

    async deleteFile(id) {
      await this.$emit("deleteFiles", id);

      this.files.forEach((file) => {
        if (file.id !== id) return file;
      });
    },

    async onChangeFiles(selectedFiles) {
      if (selectedFiles.length) {
        await this.$emit("uploadFiles", selectedFiles);
      }
    },

    getUploadFiles() {
      this.uploadFiles.forEach((file) => {
        const { id, name, link: imageUrl } = file;

        this.files.push({ id, name, imageUrl });
      });
    },

    getFormFiles() {
      this.form.image = this.files.map((file) => file.id);
    },

    emitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();

        return;
      }

      this.form.isValid = true;
      this.$emit("input", this.form);
    },
  },
};
</script>
